<template>
  <BaseCombobox
    ref="comboboxRef"
    class="shipment-item-finished-good-combobox"
    :items="options"
    :search-term="searchTerm"
    :loading="loading"
    value-key="value"
    label-key="label"
    trigger-mode="input"
    filter-mode="external"
    :placeholder="placeholder"
    :disabled-values="excludedItemIds"
    :no-results-text="searchFailed ? SEARCH_ERROR : 'No finished goods found'"
    loading-text="Searching finished goods..."
    aria-label="Finished good search"
    :show-open-button="false"
    @update:search-term="handleSearchTermUpdate"
    @focus="handleFocus"
    @select="handleSelect"
  >
    <template #option="{ option, disabled }">
      <span>{{ option.label }}</span>
      <span v-if="disabled" class="tag is-light ml-2">Already in shipment</span>
    </template>
  </BaseCombobox>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onMounted, onUnmounted } from "vue";
  import { useDebounceFn } from "@vueuse/core";

  import BaseCombobox from "./shared/base-combobox.vue";
  import { useFinishedGoodSearch } from "./stores/finished-good-search-store";
  import { Flash } from "~/jose_modern/flash";

  const SEARCH_ERROR =
    "Sorry, there was an error with your search. Please try again or contact support.";

  const props = defineProps({
    userId: { type: Number, required: true },
    placeholder: { type: String, default: "Search finished goods..." },
    autofocus: { type: Boolean, default: false },
    excludedItemIds: { type: Array, default: () => [] },
  });

  const emit = defineEmits(["select"]);

  const { results, loading, search, clear } = useFinishedGoodSearch();
  const comboboxRef = _ref(null);
  let searchTerm = _ref("");

  const options = _computed(() =>
    results.value.map((finishedGood) => ({
      value: finishedGood.id,
      label: finishedGood.recipe.name,
      raw: finishedGood,
    }))
  );

  let searchFailed = _ref(false);

  async function runSearch(query) {
    try {
      await search(props.userId, query);
      searchFailed.value = false;
    } catch {
      searchFailed.value = true;
      Flash.error(SEARCH_ERROR);
    }
  }

  const debouncedSearch = useDebounceFn((query) => runSearch(query), 300);

  function handleSearchTermUpdate(query) {
    searchTerm.value = query;
    loading.value = true;
    debouncedSearch(query);
  }

  function handleFocus() {
    loading.value = true;
    debouncedSearch(searchTerm.value);
  }

  function handleSelect(option) {
    searchFailed.value = false;
    clear();
    debouncedSearch.cancel?.();
    searchTerm.value = "";
    emit("select", option.raw);
  }

  function focus() {
    comboboxRef.value?.focusTrigger();
  }

  onMounted(() => {
    if (props.autofocus) focus();
  });

  onUnmounted(() => {
    debouncedSearch.cancel?.();
    clear();
  });

  defineExpose({ focus });
</script>

<style lang="scss" scoped>
  .shipment-item-finished-good-combobox {
    width: 100%;
  }
</style>
